import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Anchor, Button, Skeleton, TextInput } from '@mantine/core';
import { IconCircleCheck, IconCircleMinus, IconDeviceFloppy, IconPencil, IconRefresh, IconTrash } from '@tabler/icons-react';
import {
  RIDES_CONSENT_TYPE,
  RIDES_ERROR_CODES,
  RIDES_SEX,
  RIDES_VERIFICATION_STATUS,
  RIDES_VERIFICATION_TYPE,
  useMemberMeService,
  useRidesMeService,
  type RidesConsentView,
  type RidesDriverProfileView,
} from '@actschurch/ccms-sdk';
import { useEnrichedAuth } from '@/hooks/useEnrichedAuth';
import { findVerification, isSubmitted, useDriverProfile, useInvalidateMe, useMemberMe, useMyVerifications, useRiderProfile } from '@/lib/me';
import { useRidesLookups } from '@/lib/lookups';
import { ridesKeys } from '@/lib/queryKeys';
import { errorMessage, hasErrorCode } from '@/lib/copy';
import { parseApiDate, stampToDate } from '@/lib/format';
import { deleteDataMailto, driverStandards, strongerLevel, supportLink, useCopy } from '@/lib/settings';
import {
  EMPTY_EMERGENCY,
  driverUpsertFromView,
  emergencyChanged,
  emergencyFromView,
  emergencyRequest,
  emergencyValid,
  type EmergencyDraft,
} from '@/lib/driverStandards';
import { Callout, DateOfBirthInput, PhoneInput, PickerCard, formatSaPhone, normaliseAnyPhone } from '@/components/ui';
import { SummaryRow } from '@/components/rides/PageIntro';
import { PlateChip } from '@/components/rides/PlateChip';
import { VerificationPills } from '@/components/rides/VerificationPills';
import { EmergencyContactCard } from '@/components/rides/EmergencyContactCard';
import { carWords } from './trips';
import '@/styles/member.css';

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

/** "12 March 1994" (no weekday: dates of birth and expiry dates). */
export function longDate(value: string | number | Date | null | undefined): string {
  const d = value instanceof Date ? value : typeof value === 'number' && String(value).length === 14 ? stampToDate(value) : parseApiDate(value ?? null);
  return d ? `${d.getDate()} ${MONTHS[d.getMonth()]} ${d.getFullYear()}` : '';
}

/** Inline link with a 48 px tall tap area that doesn't push the text apart. */
const TALL_LINK = { display: 'inline-block', padding: '14px 4px', margin: '-14px 0' } as const;

type Sex = 'Male' | 'Female';
function normaliseSex(g: string | null | undefined): Sex | null {
  const v = (g ?? '').trim().toLowerCase();
  if (v === 'male' || v === 'm') return 'Male';
  if (v === 'female' || v === 'f') return 'Female';
  return null;
}

/** Keep the licence year when rebuilding the driver body (the API asks for it when experience is checked). */
function driverBody(d: RidesDriverProfileView, patch: Parameters<typeof driverUpsertFromView>[1]) {
  return driverUpsertFromView(d, { licenceFirstIssuedYear: d.licenceFirstIssuedYear ?? null, ...patch });
}

function SkeletonLines({ count = 3 }: { count?: number }) {
  return (
    <div className="rm-skel" aria-hidden="true">
      {Array.from({ length: count }, (_, i) => <Skeleton key={i} height={16} width={i % 2 ? '60%' : '85%'} radius="sm" />)}
    </div>
  );
}

function RetryCallout({ message, onRetry, retrying }: { message: string; onRetry: () => void; retrying?: boolean }) {
  return (
    <Callout tone="bad" action={<Button variant="default" leftSection={<IconRefresh size={18} />} loading={retrying} onClick={onRetry}>Try again</Button>}>
      {message}
    </Callout>
  );
}

// ---------------------------------------------------------------- your details

type DetailErrors = Partial<Record<'name' | 'dob' | 'sex' | 'cell', string>>;

/**
 * Name, email, cellphone number, date of birth and sex, with an Edit form. Saved to the Acts Church
 * member profile (PUT members/me) and, when the member has a Carpool rider profile, to that too
 * (PUT me/profile: name, cellphone and sex), so drivers and campus security see the same details.
 */
export function DetailsBody() {
  const { t, settings } = useCopy();
  const { user } = useEnrichedAuth();
  const member = useMemberMe();
  const rider = useRiderProfile();
  const driver = useDriverProfile();
  const members = useMemberMeService();
  const me = useRidesMeService();
  const invalidate = useInvalidateMe();
  const support = supportLink(settings);
  const formRef = useRef<HTMLDivElement>(null);

  const accountName = `${user?.firstName ?? ''} ${user?.surname ?? ''}`.trim();
  const shownName = rider.data?.displayName?.trim() || accountName;
  const currentCell = member.data?.cellNumber?.trim() || user?.cellphone?.trim() || '';
  const currentDob = member.data?.dateOfBirth ? String(member.data.dateOfBirth).slice(0, 10) : null;
  const currentSex = normaliseSex(member.data?.gender) ?? (rider.data ? (rider.data.sexID === RIDES_SEX.Female ? 'Female' : 'Male') : null);
  // Name, date of birth and sex are part of the ID check (and sex drives the safety-matching rule), so
  // once a government or guardian ID check is verified or waiting they are read-only here; the API
  // refuses a sex change with 400 sex_locked anyway.
  const checks = useMyVerifications();
  const identityLocked =
    isSubmitted(findVerification(checks.data, RIDES_VERIFICATION_TYPE.GovernmentId)) ||
    isSubmitted(findVerification(checks.data, RIDES_VERIFICATION_TYPE.GuardianId));

  const [editing, setEditing] = useState(false);
  const [saved, setSaved] = useState(false);
  const [name, setName] = useState('');
  const [cell, setCell] = useState('');
  const [dob, setDob] = useState<string | null>(null);
  const [sex, setSex] = useState<Sex | null>(null);
  const [errors, setErrors] = useState<DetailErrors>({});

  const startEdit = () => {
    setName(shownName);
    setCell(currentCell ? formatSaPhone(currentCell) : '');
    setDob(currentDob);
    setSex(currentSex);
    setErrors({});
    setSaved(false);
    setEditing(true);
  };

  useEffect(() => {
    if (!saved) return;
    const timer = window.setTimeout(() => setSaved(false), 6000);
    return () => window.clearTimeout(timer);
  }, [saved]);

  const save = useMutation({
    mutationFn: async () => {
      const cellNumber = cell.trim();
      await members.updateMe({ dateOfBirth: dob, gender: sex ?? undefined, cellNumber });
      const sexID = identityLocked && rider.data ? rider.data.sexID : sex === 'Female' ? RIDES_SEX.Female : RIDES_SEX.Male;
      if (rider.data) {
        // The API resets an empty name to the account name, so always send it.
        await me.upsertProfile({ sexID, displayName: name.trim() || rider.data.displayName, mobile: cellNumber });
      } else if (driver.data && driver.data.sexID !== sexID) {
        await me.upsertDriver(driverBody(driver.data, { sexID }));
      }
    },
    onSuccess: () => {
      invalidate();
      setEditing(false);
      setSaved(true);
    },
  });

  const submit = () => {
    const e: DetailErrors = {};
    if (rider.data && !identityLocked && name.trim().length < 2) e.name = 'Please enter your name as it is on your ID.';
    if (!dob) e.dob = 'Please choose your day, month and year of birth.';
    else if (dob > new Date().toISOString().slice(0, 10)) e.dob = 'That date is in the future. Please check it.';
    if (!sex) e.sex = 'Please choose one.';
    if (!normaliseAnyPhone(cell)) e.cell = 'Please enter your cellphone number, for example 082 123 4567.';
    setErrors(e);
    if (Object.keys(e).length) {
      window.requestAnimationFrame(() => {
        const firstBad = formRef.current?.querySelector<HTMLElement>('[data-error="true"]');
        firstBad?.scrollIntoView({ block: 'center', behavior: 'smooth' });
        firstBad?.querySelector<HTMLElement>('input, select, button')?.focus();
      });
      return;
    }
    save.mutate();
  };

  return (
    <div>
      {member.isLoading ? (
        <SkeletonLines count={4} />
      ) : member.isError ? (
        <RetryCallout message="We couldn't load your details just now." onRetry={() => member.refetch()} retrying={member.isRefetching} />
      ) : editing ? (
        <div className="rm-form" ref={formRef}>
          {save.error && <Callout tone="bad" live>{errorMessage(save.error)}</Callout>}
          {identityLocked && (
            <Callout tone="info">
              {t('account.identity_locked')} {support && <Anchor href={support.href} style={TALL_LINK}>Contact us</Anchor>}
            </Callout>
          )}
          {rider.data && !identityLocked ? (
            <div data-error={errors.name ? 'true' : undefined}>
              <TextInput
                label="Your name"
                description={t('account.name_help')}
                value={name}
                maxLength={200}
                autoComplete="name"
                onChange={(ev) => { setName(ev.currentTarget.value); setErrors((x) => ({ ...x, name: undefined })); }}
                error={errors.name}
                required
              />
            </div>
          ) : (
            <div>
              <span className="rm-fieldlabel">Your name</span>
              <div className="rm-readonly">{(identityLocked ? shownName : accountName) || 'Not added'}</div>
              {!identityLocked && (
                <p className="rm-fieldhelp">
                  {t('account.name_locked')} {support && <Anchor href={support.href} style={TALL_LINK}>Contact us</Anchor>}
                </p>
              )}
            </div>
          )}
          <div data-error={errors.cell ? 'true' : undefined}>
            <PhoneInput
              required
              value={cell}
              onChange={(v) => { setCell(v); setErrors((x) => ({ ...x, cell: undefined })); }}
              description={t('account.details_note')}
              error={errors.cell}
            />
          </div>
          {identityLocked ? (
            <>
              <div>
                <span className="rm-fieldlabel">Date of birth</span>
                <div className="rm-readonly">{currentDob ? longDate(currentDob) : 'Not added'}</div>
              </div>
              <div>
                <span className="rm-fieldlabel">Sex</span>
                <div className="rm-readonly">{currentSex ?? 'Not added'}</div>
              </div>
            </>
          ) : (<>
          <div data-error={errors.dob ? 'true' : undefined}>
            <DateOfBirthInput
              value={dob}
              onChange={(v) => { setDob(v); setErrors((x) => ({ ...x, dob: undefined })); }}
              required
              description={t('profile_gate.dob_help')}
              error={errors.dob}
            />
          </div>
          <div data-error={errors.sex ? 'true' : undefined}>
            <span id="rm-sex-label" className="rm-fieldlabel">Sex<span className="rides-req" aria-hidden="true">*</span></span>
            <div className="rides-seg" role="radiogroup" aria-labelledby="rm-sex-label" aria-describedby="rm-sex-help">
              {(['Female', 'Male'] as Sex[]).map((v) => (
                <PickerCard key={v} selected={sex === v} onSelect={() => { setSex(v); setErrors((x) => ({ ...x, sex: undefined })); }} title={v} />
              ))}
            </div>
            <p id="rm-sex-help" className="rm-fieldhelp">{t('profile_gate.sex_help')}</p>
            {errors.sex && <p className="rm-small" role="alert" style={{ color: 'var(--rides-bad)', fontWeight: 600, marginTop: 6 }}>{errors.sex}</p>}
          </div>
          </>)}
          <div className="rm-btnrow">
            <Button loading={save.isPending} onClick={submit} leftSection={<IconDeviceFloppy size={18} />}>Save my details</Button>
            <Button variant="default" disabled={save.isPending} onClick={() => { setEditing(false); save.reset(); }}>Cancel</Button>
          </div>
        </div>
      ) : (
        <>
          <div>
            <SummaryRow k="Name" v={shownName || 'Not added'} />
            <SummaryRow k="Email" v={user?.email || 'Not added'} />
            <SummaryRow k="Cellphone" v={currentCell ? formatSaPhone(currentCell) : 'Not added'} />
            <SummaryRow k="Date of birth" v={currentDob ? longDate(currentDob) : 'Not added'} />
            <SummaryRow k="Sex" v={currentSex ?? 'Not added'} />
          </div>
          <div role="status" aria-live="polite">
            {saved && <p className="rm-ok-text" style={{ marginTop: 8 }}>{t('account.details_saved')}</p>}
          </div>
          <Button variant="default" fullWidth mt="md" leftSection={<IconPencil size={18} />} onClick={startEdit} aria-label="Edit your details">
            Edit my details
          </Button>
        </>
      )}
    </div>
  );
}

/** "082 555 0142" (or a hint) for the Personal details row. */
export function useDetailsSummary(): string {
  const { user } = useEnrichedAuth();
  const member = useMemberMe();
  const cell = member.data?.cellNumber?.trim() || user?.cellphone?.trim() || '';
  return cell ? formatSaPhone(cell) : 'Cellphone, date of birth and sex';
}

// ---------------------------------------------------------------- driving

/** The driver's car, seats and licence, with a way to change them (the driver details step). */
export function CarBody() {
  const { t, settings } = useCopy();
  const driver = useDriverProfile();
  const support = supportLink(settings);
  const d = driver.data;
  if (!d) return null;
  const canEdit = settings.driverRegistrationOpen && !settings.maintenanceMode;
  const licence = d.licence.licenceCode ? `Code ${d.licence.licenceCode}` : d.licence.licenceTypeID === 2 ? 'Foreign licence' : "Driver's licence";
  return (
    <div>
      {d.isSuspended && (
        <div style={{ marginBottom: 8 }}>
          <Callout tone="bad">
            {t('account.suspended')}
            {d.suspendedReason ? ` ${d.suspendedReason}` : ''}
          </Callout>
        </div>
      )}
      <SummaryRow k="Car" v={<span style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6 }}>{carWords(d.vehicle)}<PlateChip plate={d.vehicle.plate} /></span>} />
      <SummaryRow k="Seats you offer" v={d.vehicle.seats} />
      <SummaryRow k="Licence" v={licence} sub={d.licence.expiresAt ? `Valid until ${longDate(d.licence.expiresAt)}` : undefined} />
      <p className="rm-small" style={{ marginTop: 8 }}>
        {canEdit ? t('account.car_help') : <>{t('account.car_contact')} {support && <Anchor href={support.href} style={TALL_LINK}>Contact us</Anchor>}</>}
      </p>
      {canEdit && (
        <Button component={Link} to="/drive/profile" variant="default" fullWidth mt="md" leftSection={<IconPencil size={18} />}>
          Change my car or licence
        </Button>
      )}
    </div>
  );
}

// ---------------------------------------------------------------- ID checks

/** ID (and licence, guardian) checks in plain words, honest while loading or when the request fails. */
export function ChecksBody() {
  const { t } = useCopy();
  const verifications = useMyVerifications();
  const driver = useDriverProfile();
  const { data: lookups } = useRidesLookups();
  const list = verifications.data;
  const guardian = findVerification(list, RIDES_VERIFICATION_TYPE.GuardianId);
  return (
    <div>
      <p className="rm-small" style={{ marginBottom: 10, fontSize: 15 }}>{t('account.checks_intro')}</p>
      {verifications.isLoading ? (
        <SkeletonLines count={3} />
      ) : verifications.isError ? (
        <RetryCallout message={t('account.checks_error')} onRetry={() => verifications.refetch()} retrying={verifications.isRefetching} />
      ) : (
        <VerificationPills
          variant="list"
          providerName={lookups?.settings?.verificationProvider}
          items={[
            { typeId: RIDES_VERIFICATION_TYPE.GovernmentId, verification: findVerification(list, RIDES_VERIFICATION_TYPE.GovernmentId) },
            ...(driver.data ? [{ typeId: RIDES_VERIFICATION_TYPE.DriversLicence, verification: findVerification(list, RIDES_VERIFICATION_TYPE.DriversLicence) }] : []),
            ...(guardian ? [{ typeId: RIDES_VERIFICATION_TYPE.GuardianId, verification: guardian }] : []),
          ]}
        />
      )}
    </div>
  );
}

/** Overall ID check status for the row: Checked / Waiting / Needs attention / Not done yet. */
export function useChecksSummary(): { label: string; tone: 'ok' | 'warn' | 'bad' | 'neutral' } | null {
  const verifications = useMyVerifications();
  const driver = useDriverProfile();
  if (!verifications.isSuccess) return null;
  const list = [
    findVerification(verifications.data, RIDES_VERIFICATION_TYPE.GovernmentId),
    ...(driver.data ? [findVerification(verifications.data, RIDES_VERIFICATION_TYPE.DriversLicence)] : []),
  ];
  if (list.some((v) => v && (v.statusID === RIDES_VERIFICATION_STATUS.Failed || v.statusID === RIDES_VERIFICATION_STATUS.Expired || v.statusID === RIDES_VERIFICATION_STATUS.Revoked))) {
    return { label: 'Needs attention', tone: 'bad' };
  }
  if (list.every((v) => v?.statusID === RIDES_VERIFICATION_STATUS.Verified)) return { label: 'Checked', tone: 'ok' };
  if (list.some((v) => v?.statusID === RIDES_VERIFICATION_STATUS.Pending)) return { label: 'Waiting', tone: 'warn' };
  return { label: 'Not done yet', tone: 'neutral' };
}

// ---------------------------------------------------------------- emergency contact

/**
 * Emergency contact (254g): shown when the settings ask it of riders, or of drivers and this
 * member drives. Saved through the rider profile (the contact lives on the member record), or the
 * driver profile when only that exists.
 */
export function EmergencyBody() {
  const { t, settings } = useCopy();
  const standards = driverStandards(settings);
  const me = useRidesMeService();
  const profile = useRiderProfile();
  const driver = useDriverProfile();
  const invalidate = useInvalidateMe();
  const [draft, setDraft] = useState<EmergencyDraft>(EMPTY_EMERGENCY);
  const [saved, setSaved] = useState(false);
  const current = profile.data?.emergencyContact ?? driver.data?.emergencyContact ?? null;

  useEffect(() => {
    setDraft(emergencyFromView(current));
  }, [current?.name, current?.phone, current?.relationship]); // eslint-disable-line react-hooks/exhaustive-deps

  const level = strongerLevel(standards.riderEmergency, driver.data ? standards.driverEmergency : 'notAsked');
  const save = useMutation({
    mutationFn: async () => {
      const emergencyContact = emergencyRequest(draft);
      if (profile.data) {
        // Send the name and cellphone too: the API resets an empty name to the account name.
        return me.upsertProfile({ sexID: profile.data.sexID, displayName: profile.data.displayName, emergencyContact });
      }
      if (driver.data) return me.upsertDriver(driverBody(driver.data, { emergencyContact }));
      return null;
    },
    onSuccess: () => {
      setSaved(true);
      invalidate();
    },
  });

  useEffect(() => {
    if (!saved) return;
    const timer = window.setTimeout(() => setSaved(false), 6000);
    return () => window.clearTimeout(timer);
  }, [saved]);

  if (level === 'notAsked' || (!profile.data && !driver.data)) return null;
  const changed = emergencyChanged(draft, current);
  const valid = emergencyValid(draft, level);
  const missing = hasErrorCode(save.error, RIDES_ERROR_CODES.EmergencyContactRequired);

  return (
    <div>
      <EmergencyContactCard
        plain
        value={draft}
        onChange={(v) => { setDraft(v); setSaved(false); }}
        level={level}
        highlight={missing}
        disabled={save.isPending}
        footer={
          <>
            {save.error && !missing && <Callout tone="bad" live>{errorMessage(save.error)}</Callout>}
            {changed && (
              <div className="rm-btnrow">
                <Button leftSection={<IconDeviceFloppy size={18} />} disabled={!valid} loading={save.isPending} onClick={() => save.mutate()}>
                  Save emergency contact
                </Button>
                <Button variant="default" disabled={save.isPending} onClick={() => { setDraft(emergencyFromView(current)); save.reset(); }}>
                  Cancel
                </Button>
              </div>
            )}
            {changed && !valid && <p className="rm-small">{level === 'optional' ? 'Please add their name and a phone number we can call, or clear all three boxes.' : 'Please add their name and a phone number we can call.'}</p>}
            <div role="status" aria-live="polite">
              {saved && !changed && <p className="rm-ok-text">{t('emergency_card.saved')}</p>}
            </div>
          </>
        }
      />
    </div>
  );
}

/** Whether Account shows the emergency contact row, with its summary (name or "Not added"). */
export function useEmergencySummary(): { visible: boolean; summary: string; needed: boolean } {
  const { settings } = useCopy();
  const standards = driverStandards(settings);
  const profile = useRiderProfile();
  const driver = useDriverProfile();
  const level = strongerLevel(standards.riderEmergency, driver.data ? standards.driverEmergency : 'notAsked');
  const current = profile.data?.emergencyContact ?? driver.data?.emergencyContact ?? null;
  const name = current?.name?.trim();
  const visible = level !== 'notAsked' && (!!profile.data || !!driver.data);
  return { visible, summary: name ? `${name}${current?.relationship ? ` (${current.relationship.trim().toLowerCase()})` : ''}` : 'Not added', needed: level === 'required' && !name };
}

// ---------------------------------------------------------------- what you agreed to

const CONSENT_WORDS: Record<number, { title: string; text: string }> = {
  [RIDES_CONSENT_TYPE.DataProcessing]: { title: 'Privacy and your information', text: 'Acts Church may use your details to arrange lifts.' },
  [RIDES_CONSENT_TYPE.GuardianMinor]: { title: 'Consent for a child', text: 'As a parent or guardian, you agreed that a child may ride.' },
  [RIDES_CONSENT_TYPE.DriverDeclaration]: { title: 'Driver promise', text: 'You accepted the driver declaration.' },
  [RIDES_CONSENT_TYPE.SpecialInformation]: { title: 'Child-safety clearance', text: 'We may check your clearance to decide if you can drive under-18s.' },
};

/** The newest record per consent type, in plain words (no codes, no version strings). */
function latestPerType(list: RidesConsentView[]): RidesConsentView[] {
  const byType = new Map<number, RidesConsentView>();
  for (const c of list) {
    const prev = byType.get(c.consentTypeID);
    if (!prev || Number(c.createdDateTime) > Number(prev.createdDateTime)) byType.set(c.consentTypeID, c);
  }
  return [...byType.values()].sort((a, b) => a.consentTypeID - b.consentTypeID);
}

export function ConsentsBody() {
  const { t } = useCopy();
  const me = useRidesMeService();
  const { isAuthenticated } = useEnrichedAuth();
  const consents = useQuery({ queryKey: ridesKeys.me.consents, queryFn: () => me.getConsents(), enabled: isAuthenticated, staleTime: 60_000 });
  const rows = latestPerType(consents.data ?? []);
  return (
    <div>
      <p className="rm-small" style={{ fontSize: 15 }}>{t('account.consents_intro')}</p>
      {consents.isLoading ? (
        <SkeletonLines count={2} />
      ) : consents.isError ? (
        <RetryCallout message="We couldn't load this just now." onRetry={() => consents.refetch()} retrying={consents.isRefetching} />
      ) : rows.length === 0 ? (
        <p className="rm-muted">{t('account.consents_empty')}</p>
      ) : (
        <div>
          {rows.map((c) => {
            const words = CONSENT_WORDS[c.consentTypeID] ?? { title: 'Consent', text: '' };
            const when = longDate(c.createdDateTime);
            return (
              <div key={c.consentTypeID} className={`rm-consent${c.granted ? '' : ' off'}`}>
                <span className="ic" aria-hidden="true">{c.granted ? <IconCircleCheck size={22} /> : <IconCircleMinus size={22} />}</span>
                <div style={{ minWidth: 0 }}>
                  <b>{words.title}</b>
                  {words.text && <span>{words.text}</span>}
                  <span>{c.granted ? 'Agreed' : 'Withdrawn'}{when ? ` on ${when}` : ''}</span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------- delete my data

export function DeleteBody() {
  const { t, settings } = useCopy();
  const appName = t('app.name');
  return (
    <div>
      <p className="rm-muted" style={{ color: 'var(--rides-ink)', marginBottom: 12 }}>{t('account.delete_text')}</p>
      <Button component="a" href={deleteDataMailto(settings, appName)} variant="outline" color="red" fullWidth leftSection={<IconTrash size={18} />}>
        Ask us to delete my data
      </Button>
      {settings.deleteDataEmail && <p className="rm-small" style={{ marginTop: 8 }}>{t('account.delete_note', undefined, { email: settings.deleteDataEmail })}</p>}
    </div>
  );
}
